// Multi-select watcher picker — replaces the simple Watch/Unwatch toggle
// in the ticket-detail Watchers panel. Click the trigger to open a
// dropdown listing every active teammate with an inline toggle; click
// any row to subscribe or unsubscribe them from this ticket.
//
// Why a picker (vs the old single button):
//   - "Liz, you should keep an eye on this" used to require Liz to open
//     the ticket and click Watch herself. Now anyone on the team can add
//     her in two clicks from the sidebar — same as how Reassign already
//     works.
//   - Self stays the first row + most prominent so the common case
//     ("watch this ticket myself") is still one click.
//
// Permissions: migration 041 widened the ticket_watchers RLS so any
// active team member can insert / delete any watcher row. RLS is still
// on, so inactive members get a postgres error rather than a silent
// success.
//
// We reuse the existing `useToggleWatch` mutation — it already accepts a
// `slackUserId` parameter and invalidates the right caches (per-ticket
// watcher list, per-user "am I watching this", and the global "my
// watched ticket ids" used by My Day + the dashboard Watching tab).

import { useMemo, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/UserAvatar';
import {
  PickerCheck,
  PickerEmpty,
  PickerSearch,
  PICKER_ROW_CLASS,
} from '@/components/TeammatePickerParts';
import {
  useTeamMembers,
  useTicket,
  useToggleWatch,
  type TeamMember,
  type Watcher,
} from '@/lib/queries';
import { useAuth } from '@/lib/auth-guard';
import { useAccess } from '@/lib/use-access';
import { ChevronDown, Eye, EyeOff } from 'lucide-react';

interface Props {
  ticketId: string;
  /** Current watcher rows (from useTicketWatchers). Drives the checks
   *  on each list row + the "I'm watching" / "Not watching" state of
   *  the trigger button. */
  watchers: Watcher[];
  teamMembersBySlackId?: Map<string, TeamMember>;
}

export function WatcherPicker({ ticketId, watchers, teamMembersBySlackId }: Props) {
  const { teamMember } = useAuth();
  const mySlack = teamMember?.slack_user_id ?? null;
  const myEmail = teamMember?.email ?? null;
  const teamQ = useTeamMembers();
  const toggleWatch = useToggleWatch();
  // Anyone who can see the ticket can watch it; adding SOMEONE ELSE as a
  // watcher needs watchers.manage_others in this ticket's inbox (078).
  const access = useAccess();
  const inboxId = useTicket(ticketId).data?.inbox_id ?? null;
  const canManageOthers = access.canIn(inboxId, 'watchers.manage_others');

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // O(1) "is X watching" lookups + a quick self check for the trigger.
  const watchingIds = useMemo(
    () => new Set(watchers.map((w) => w.slack_user_id)),
    [watchers],
  );
  const isSelfWatching = !!mySlack && watchingIds.has(mySlack);

  // Active teammates only. Self surfaces at the top as a pinned row;
  // everyone else is alphabetised so the list is predictable.
  const { selfMember, others } = useMemo(() => {
    const all = (teamQ.data ?? []).filter((m) => m.active);
    const self = mySlack ? all.find((m) => m.slack_user_id === mySlack) ?? null : null;
    const rest = all.filter((m) => m.slack_user_id !== mySlack);
    rest.sort((a, b) =>
      (a.name ?? a.email ?? '').localeCompare(b.name ?? b.email ?? ''),
    );
    const term = search.trim().toLowerCase();
    const filtered = term
      ? rest.filter((m) =>
          `${m.name ?? ''} ${m.email ?? ''}`.toLowerCase().includes(term),
        )
      : rest;
    return { selfMember: self, others: filtered };
  }, [teamQ.data, mySlack, search]);

  const toggle = (slackUserId: string) => {
    const watching = !watchingIds.has(slackUserId);
    // Pass the actor's identity so useToggleWatch can ping the
    // recipient with an `added_as_watcher` notification when someone
    // adds a teammate (not themselves). See migration 042.
    toggleWatch.mutate({
      ticketId,
      watching,
      slackUserId,
      actorSlackId: mySlack,
      actorEmail: myEmail,
    });
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant={isSelfWatching ? 'outline' : 'ghost'}
          disabled={toggleWatch.isPending || !mySlack}
        >
          {isSelfWatching ? (
            <EyeOff className="h-3.5 w-3.5" />
          ) : (
            <Eye className="h-3.5 w-3.5" />
          )}
          {isSelfWatching ? 'Watching' : 'Watch'}
          <ChevronDown className="h-3 w-3 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 p-0">
        {/* Search — useful once the team grows past a screenful. */}
        {canManageOthers ? (
          <div className="border-b border-border/60 p-2">
            <PickerSearch autoFocus value={search} onChange={setSearch} />
          </div>
        ) : null}

        <div className="max-h-72 overflow-y-auto py-1">
          {/* Self row — pinned to the top so "watch this myself" stays a
              one-click action, just like the old Watch button. */}
          {selfMember ? (
            <button
              type="button"
              onClick={() => toggle(selfMember.slack_user_id)}
              disabled={toggleWatch.isPending}
              // Watching is a multi-select toggle, so the row has to announce
              // its own checked state — the visual check square is aria-hidden
              // decoration. Without this a screen-reader user toggling
              // watchers heard nothing about what was on or off.
              role="menuitemcheckbox"
              aria-checked={isSelfWatching}
              className={PICKER_ROW_CLASS}
            >
              <UserAvatar
                slackId={selfMember.slack_user_id}
                teamMembersBySlackId={teamMembersBySlackId}
                size="sm"
              />
              <div className="min-w-0 flex-1">
                <p className="font-medium">{selfMember.name ?? selfMember.email} <span className="text-muted-foreground">(you)</span></p>
              </div>
              <PickerCheck on={isSelfWatching} />
            </button>
          ) : null}

          {selfMember && canManageOthers && others.length > 0 ? (
            <div className="my-1 border-t border-border/40" />
          ) : null}

          {!canManageOthers ? null : others.length === 0 ? (
            <PickerEmpty searching={!!search.trim()} />
          ) : (
            others.map((m) => {
              const watching = watchingIds.has(m.slack_user_id);
              return (
                <button
                  key={m.slack_user_id}
                  type="button"
                  onClick={() => toggle(m.slack_user_id)}
                  disabled={toggleWatch.isPending}
                  role="menuitemcheckbox"
                  aria-checked={watching}
                  className={PICKER_ROW_CLASS}
                >
                  <UserAvatar
                    slackId={m.slack_user_id}
                    teamMembersBySlackId={teamMembersBySlackId}
                    size="sm"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{m.name ?? m.email}</p>
                    {m.name ? (
                      <p className="truncate text-3xs text-muted-foreground">
                        {m.email}
                      </p>
                    ) : null}
                  </div>
                  <PickerCheck on={watching} />
                </button>
              );
            })
          )}
        </div>

        <div className="border-t border-border/60 px-2.5 py-1.5 text-2xs text-muted-foreground">
          {watchers.length === 0
            ? 'No watchers yet.'
            : `${watchers.length} ${watchers.length === 1 ? 'watcher' : 'watchers'}`}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

